import React from "react";

// Auth-gated media, playable in plain tags.
//
// Every corrido still, storyboard thumbnail and finished video is served by
// /api/web/corrido/image (and /api/web/story/media) behind authenticateToken,
// which accepts ONLY an Authorization: Bearer header. A browser <img src> /
// <video src> cannot send that header, so pointing tags straight at those
// routes returned 401 for every single image — the storyboard rendered as a
// wall of broken thumbnails even though the stills existed.
//
// Fix: fetch the bytes WITH the token and hand tags a blob: URL (CSP imgSrc /
// mediaSrc already allow blob:). Deliberately NOT a token in the query string —
// that leaks a credential into history, logs and referrers (same call as the
// story-video fix in StoryChatFlow).
//
// The module-level cache makes each unique path a single fetch per session —
// a 30-shot storyboard re-rendering its React tree must not re-download shots.

const cache = new Map<string, Promise<string>>();

function needsAuth(url: string): boolean {
  return url.startsWith("/api/");
}

export function resolveAuthMediaUrl(url: string): Promise<string> {
  if (!needsAuth(url)) return Promise.resolve(url);
  let hit = cache.get(url);
  if (!hit) {
    hit = (async () => {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem("auth_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const r = await fetch(url, { headers });
      if (!r.ok) throw new Error(`media fetch failed (HTTP ${r.status})`);
      return URL.createObjectURL(await r.blob());
    })();
    // A failed fetch must not poison the cache — let the next render retry.
    hit.catch(() => cache.delete(url));
    cache.set(url, hit);
  }
  return hit;
}

/** Blob-backed URL for an auth-gated path ("" until it resolves), and whether
 *  the fetch failed — so a player can say so instead of loading forever.
 *  `retry` fetches again (a failed fetch is never cached). */
export function useAuthMedia(url: string | null | undefined): { url: string; failed: boolean; retry: () => void } {
  const [resolved, setResolved] = React.useState(() => (url && !needsAuth(url) ? url : ""));
  const [failed, setFailed] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    setFailed(false);
    if (!url) { setResolved(""); return; }
    if (!needsAuth(url)) { setResolved(url); return; }
    let alive = true;
    setResolved("");
    resolveAuthMediaUrl(url).then(
      (u) => { if (alive) setResolved(u); },
      () => { if (alive) { setResolved(""); setFailed(true); } },
    );
    return () => { alive = false; };
  }, [url, attempt]);
  const retry = React.useCallback(() => setAttempt((n) => n + 1), []);
  return { url: resolved, failed, retry };
}

/** Blob-backed URL for an auth-gated path ("" until it resolves). blob:, data:
 *  and external URLs pass straight through. */
export function useAuthMediaUrl(url: string | null | undefined): string {
  return useAuthMedia(url).url;
}

type ImgProps = React.ImgHTMLAttributes<HTMLImageElement>;

/** Drop-in <img> for auth-gated sources. */
export function AuthImg({ src, style, ...rest }: ImgProps) {
  const resolved = useAuthMediaUrl(typeof src === "string" ? src : null);
  if (!resolved) {
    // Reserve the box while the blob loads so layouts don't jump.
    return <span aria-hidden style={{ ...(style as React.CSSProperties), display: "inline-block", background: "rgba(255,255,255,.04)" }} />;
  }
  return <img src={resolved} style={style} {...rest} />;
}

type VideoProps = React.VideoHTMLAttributes<HTMLVideoElement>;

/** Drop-in <video> for auth-gated sources. */
export function AuthVideo({ src, style, ...rest }: VideoProps) {
  const resolved = useAuthMediaUrl(typeof src === "string" ? src : null);
  if (!resolved) {
    return <span aria-hidden style={{ ...(style as React.CSSProperties), display: "inline-block", background: "rgba(255,255,255,.04)" }} />;
  }
  return <video src={resolved} style={style} {...rest} />;
}
